/**
 * HeaderNavDrawer — menu lateral (drawer) aberto pelo hambúrguer do Header.
 *
 * Referência: docs/layout/Complemento.png ("Menu (Mobile)"), medido em
 * docs/specs/header-nav.md §2. Estrutura, de cima para baixo:
 *   fechar (X) + logo · campo de busca · idioma (só mobile) · itens · redes
 *   sociais.
 *
 * Sem chevron nos itens (decisão 2026-09-08): não há subníveis, então a seta
 * prometia um drill-down que não existe. O idioma vive no header a partir de
 * `md`; abaixo disso ele não cabe ao lado das duas marcas (mockup de
 * 2026-09-30) e vem para cá — a linha é escondida por CSS no desktop.
 * Ver docs/specs/header-nav.md §2.3c e §8.
 */
import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';
import clearSVG from '@plone/volto/icons/clear.svg';
import zoomSVG from '@plone/volto/icons/zoom.svg';

import SocialLinks from '../SocialLinks/SocialLinks';
import SocialNetworksProvider from '../SocialNetworksProvider/SocialNetworksProvider';
import HeaderLanguageSelector from './HeaderLanguageSelector';
import logoOdcConference from '../../assets/logos/logo-odc-conference.svg';

const messages = defineMessages({
  menu: {
    id: 'header.menu',
    defaultMessage: 'Menu',
  },
  closeMenu: {
    id: 'header.closeMenu',
    defaultMessage: 'Close menu',
  },
  search: {
    id: 'header.search',
    defaultMessage: 'Search',
  },
  searchPlaceholder: {
    id: 'header.searchPlaceholder',
    defaultMessage: 'Search',
  },
  home: {
    id: 'header.nav.home',
    defaultMessage: 'Home',
  },
  news: {
    id: 'header.nav.news',
    defaultMessage: 'News',
  },
  faq: {
    id: 'header.nav.faq',
    defaultMessage: 'Frequently Asked Questions',
  },
});

/**
 * Itens do menu. É chrome global (como Header/Footer): configurado no código,
 * não vem da árvore de conteúdo do Plone. Para acrescentar um item, basta
 * adicionar aqui + a string em `messages` (e rodar `make i18n`).
 *
 * `path` é o segmento por idioma, relativo à raiz do idioma — os ids do
 * conteúdo são traduzidos (`/pt-br/noticias` mas `/en/news`), então um caminho
 * único deixaria o menu inglês apontando para a URL em português. Idioma sem
 * entrada própria cai no inglês.
 */
const NAV_ITEMS: {
  id: string;
  path: Record<string, string>;
  message: any;
}[] = [
  { id: 'home', path: { en: '' }, message: messages.home },
  {
    id: 'news',
    path: { en: 'news', 'pt-br': 'noticias' },
    message: messages.news,
  },
  { id: 'faq', path: { en: 'faq' }, message: messages.faq },
];

const FOCUSABLE =
  'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

export type HeaderNavDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const HeaderNavDrawer = ({ open, onClose }: HeaderNavDrawerProps) => {
  const intl = useIntl();
  const history = useHistory();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');

  const currentLang = useSelector((s: any) => s.intl?.locale);
  // `toBackendLang` quebra com undefined — cai no idioma padrão do site.
  const lang = toBackendLang(
    currentLang || config.settings?.defaultLanguage || 'en',
  );

  // Trava o scroll da página enquanto o menu está aberto e devolve o valor
  // original ao fechar (não assume que era "visible").
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Foco entra no botão de fechar ao abrir.
  useEffect(() => {
    if (open) {
      closeRef.current?.focus();
    }
  }, [open]);

  // Esc fecha (de qualquer lugar da página); Tab circula dentro do drawer.
  // Listener no document, não no elemento: o `role="dialog"` é não-interativo
  // e receber handler de teclado nele viola jsx-a11y.
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables =
        drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusables?.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const term = query.trim();
    if (!term) return;
    onClose();
    history.push(`/search?SearchableText=${encodeURIComponent(term)}`);
  };

  return (
    <div className="header-nav">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="header-nav__overlay" onClick={onClose} />

      <div
        className="header-nav__drawer"
        id="header-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage(messages.menu)}
        ref={drawerRef}
      >
        <div className="header-nav__top">
          <button
            type="button"
            className="header-nav__close"
            aria-label={intl.formatMessage(messages.closeMenu)}
            onClick={onClose}
            ref={closeRef}
          >
            <Icon name={clearSVG} size="24px" />
          </button>
          <img className="header-nav__logo" src={logoOdcConference} alt="" />
        </div>

        <form
          className="header-nav__search"
          role="search"
          onSubmit={handleSearch}
        >
          <button
            type="submit"
            className="header-nav__search-submit"
            aria-label={intl.formatMessage(messages.search)}
          >
            <Icon name={zoomSVG} size="20px" />
          </button>
          <input
            type="search"
            className="header-nav__search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            aria-label={intl.formatMessage(messages.search)}
          />
        </form>

        <div className="header-nav__body">
          <HeaderLanguageSelector
            variant="full"
            className="header-nav__language"
            onNavigate={onClose}
          />

          <nav aria-label={intl.formatMessage(messages.menu)}>
            <ul className="header-nav__list">
              {NAV_ITEMS.map((item) => {
                const path = item.path[lang] ?? item.path.en;
                return (
                  <li key={item.id} className="header-nav__item">
                    <Link
                      className="header-nav__link"
                      to={path ? `/${lang}/${path}` : `/${lang}`}
                      onClick={onClose}
                    >
                      {intl.formatMessage(item.message)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className="header-nav__footer">
          <SocialNetworksProvider>
            {/* 20px: altura medida nos ícones do Complemento.png */}
            <SocialLinks className="header-nav__social" iconSize="20px" />
          </SocialNetworksProvider>
        </div>
      </div>
    </div>
  );
};

export default HeaderNavDrawer;
